import { LoaderCircle, LockKeyhole } from "lucide-react";
import type { PropsWithChildren } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import type { AppRole } from "./types";

export function ProtectedRoute({
  children,
  allowedRoles,
}: PropsWithChildren<{ allowedRoles?: readonly AppRole[] }>) {
  const auth = useAuth();
  const location = useLocation();

  if (auth.loading) {
    return (
      <div className="auth-status" role="status">
        <LoaderCircle className="spin" aria-hidden="true" />
        <p>Looking for your Atlas…</p>
      </div>
    );
  }
  if (!auth.user) {
    return <Navigate to="/sign-in" replace state={{ returnTo: location.pathname }} />;
  }
  if (!auth.profile) {
    return (
      <Navigate
        to="/account/recovery"
        replace
        state={{ returnTo: `${location.pathname}${location.search}` }}
      />
    );
  }
  if (!auth.profile.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  if (auth.profile.status !== "approved") {
    return <Navigate to={`/account/${auth.profile.status}`} replace />;
  }
  if (allowedRoles && !allowedRoles.includes(auth.profile.role)) {
    return (
      <div className="auth-status">
        <LockKeyhole aria-hidden="true" />
        <h1>This area is not assigned to your role.</h1>
        <p>Your account is active, but you do not have permission to view this page.</p>
      </div>
    );
  }
  return children;
}
